import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaClipboardCheck, FaSyncAlt, FaCheck, FaTimes, FaHourglassHalf } from 'react-icons/fa';
import { fmtDateTime, compactJson } from './aiTrace/format';
import './AiProposalsPanel.css';

// The proposal ledger of a ticket (backend plan H2): every effectful tool call
// of the AI is a typed proposal — pending (waiting for an operator, or a
// deferred effect applied with the reply), committed, refused, failed, expired.
// Pending human proposals are decided here.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const STATUS_ORDER = ['pending', 'committed', 'failed', 'refused', 'expired'];

const AiProposalsPanel = ({ ticketId, onChanged }) => {
    const { t, i18n } = useTranslation();
    const [proposals, setProposals] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const load = useCallback(async () => {
        if (!ticketId) return;
        setError(null);
        try {
            const response = await axios.get(`/api/tickets/${ticketId}/ai-proposals`, authHeaders());
            setProposals(response.data.proposals || []);
        } catch (e) {
            setProposals([]);
            setError(t('aiProposals.loadError', 'Impossible de charger les propositions.'));
        }
    }, [ticketId, t]);

    useEffect(() => { load(); }, [load]);

    const decide = async (proposal, action) => {
        setBusy(proposal.id);
        setError(null);
        try {
            const body = action === 'refuse' ? { reason: '' } : {};
            await axios.post(`/api/ai-proposals/${proposal.id}/${action}`, body, authHeaders());
            await load();
            if (onChanged) onChanged();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('aiProposals.decideError', 'La décision n’a pas pu être appliquée.'));
        } finally {
            setBusy(null);
        }
    };

    const isHumanPending = (p) => p.status === 'pending' && p.decision?.mode === 'human';
    const statusLabel = (p) => {
        if (p.status === 'pending' && p.decision?.mode !== 'human') return t('aiProposals.status.deferred', 'appliqué avec la réponse');
        return t(`aiProposals.status.${p.status}`, p.status);
    };

    const renderProposal = (p) => (
        <div key={p.id} className={`apr-item apr-${p.status} ${isHumanPending(p) ? 'apr-awaiting' : ''}`}>
            <div className="apr-item-head">
                <span className={`apr-pill apr-pill-${p.status}`}>
                    {isHumanPending(p) && <FaHourglassHalf />} {statusLabel(p)}
                </span>
                <span className="apr-tool">{(p.tool || '').replace(/_/g, ' ')}</span>
                <span className="apr-muted">{fmtDateTime(p.created_at, i18n.language)}</span>
            </div>
            <div className="apr-explain">{p.explain}</div>
            {p.payload && Object.keys(p.payload).length > 0 && (
                <div className="apr-payload" title={compactJson(p.payload, 600)}>{compactJson(p.payload, 180)}</div>
            )}
            <div className="apr-meta">
                {p.decision?.rule && (
                    <span>{t('aiProposals.rule', 'règle')} : {p.decision.rule}{p.decision.reason ? ` — ${p.decision.reason}` : ''}</span>
                )}
                {p.decided_by?.type && p.status !== 'pending' && (
                    <span> · {t('aiProposals.decidedBy', 'décidé par')} {p.decided_by.type}</span>
                )}
                {p.error && <span className="apr-error-inline"> · {p.error.type}: {compactJson(p.error.message, 120)}</span>}
            </div>
            {isHumanPending(p) && (
                <div className="apr-actions">
                    <button type="button" className="apr-btn apr-approve" disabled={busy === p.id} onClick={() => decide(p, 'approve')}>
                        <FaCheck /> {t('aiProposals.approve', 'Approuver et exécuter')}
                    </button>
                    <button type="button" className="apr-btn apr-refuse" disabled={busy === p.id} onClick={() => decide(p, 'refuse')}>
                        <FaTimes /> {t('aiProposals.refuse', 'Refuser')}
                    </button>
                </div>
            )}
        </div>
    );

    const pending = (proposals || []).filter(isHumanPending);
    const history = (proposals || []).filter((p) => !isHumanPending(p))
        .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || (b.created_at || '').localeCompare(a.created_at || ''));

    return (
        <div className="ai-proposals-panel">
            <div className="apr-header">
                <h3>
                    <FaClipboardCheck /> {t('aiProposals.title', 'Propositions de l’IA')}
                    {proposals && <span className="apr-muted"> ({proposals.length})</span>}
                    {pending.length > 0 && <span className="apr-badge">{pending.length} {t('aiProposals.awaiting', 'à valider')}</span>}
                </h3>
                <button type="button" className="apr-refresh" onClick={load} title={t('aiProposals.refresh', 'Actualiser')}><FaSyncAlt /></button>
            </div>
            <p className="apr-intro">
                {t('aiProposals.intro', 'Chaque action de l’IA sur ce ticket est une proposition : exécutée aussitôt, appliquée avec la réponse, ou en attente de votre validation selon la politique du compte.')}
            </p>
            {error && <div className="apr-error">{error}</div>}
            {proposals === null && !error && <div className="apr-muted">{t('aiProposals.loading', 'Chargement…')}</div>}
            {proposals && proposals.length === 0 && !error && (
                <div className="apr-empty">{t('aiProposals.empty', 'Aucune proposition enregistrée pour ce ticket.')}</div>
            )}
            {pending.map(renderProposal)}
            {history.length > 0 && (
                <>
                    <button type="button" className="apr-toggle" onClick={() => setShowHistory((v) => !v)}>
                        {showHistory ? t('aiProposals.hideHistory', 'Masquer l’historique') : t('aiProposals.showHistory', 'Historique ({{count}})', { count: history.length })}
                    </button>
                    {showHistory && history.map(renderProposal)}
                </>
            )}
        </div>
    );
};

export default AiProposalsPanel;
